import { useEffect, useRef } from 'react';
import { getShortcuts, isTypingTarget, matchesChord } from '../hooks/use-shortcut';
import type { ResolvedAction } from './registry';
import { useRunAction } from './runner';

/**
 * Binds the keymap chords of registry actions (DESIGN.md §18): pass the actions resolved for the
 * current selection with source `shortcut`. Disabled and planned actions are not bound, so their
 * chords keep the browser default. Plain keys (Delete, Insert) do not fire in text fields.
 */
export function useActionShortcuts<T>(actions: readonly ResolvedAction<T>[]): void {
  const run = useRunAction();
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      for (const entry of latest.current) {
        const shortcut = entry.action.shortcut;
        if (!entry.enabled || !shortcut) continue;
        const chord = getShortcuts(shortcut).find((candidate) => matchesChord(event, candidate));
        if (!chord) continue;
        if (!chord.includes('+') && isTypingTarget(event.target)) return;
        event.preventDefault();
        run(entry);
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [run]);
}
